import { useCallback } from 'react';

import { useSnackbar } from 'notistack';

import getChannelMarkupErrorMessage from '~/components/ChannelMarkup/getChannelMarkupErrorMessage';
import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';

import { useDeleteChannelMarkupV2 } from '~/hooks/apis/promo/useDeleteChannelMarkupV2';

/**
 * Shared delete flow for Channel Markup v2 (list + edit pages): confirm, delete, then notify.
 * `confirmAndDelete` resolves to true only when the markup was actually deleted.
 */
export default function useConfirmDeleteChannelMarkupV2() {
  const { enqueueSnackbar } = useSnackbar();
  const showConfirmDialog = useConfirmDialog();
  const deleteChannelMarkupMutation = useDeleteChannelMarkupV2();
  const { mutateAsync: deleteChannelMarkup } = deleteChannelMarkupMutation;

  const confirmAndDelete = useCallback(
    async (channelMarkupId: string): Promise<boolean> => {
      const confirmed = await showConfirmDialog({
        title: 'Delete channel markup?',
        description: `This permanently deletes channel markup ${channelMarkupId}. This cannot be undone.`,
      });

      if (!confirmed) {
        return false;
      }

      try {
        await deleteChannelMarkup(channelMarkupId);
        enqueueSnackbar('Channel Markup v2 deleted successfully', { variant: 'success' });
        return true;
      } catch (error) {
        enqueueSnackbar(`Channel Markup v2 deletion failed: ${getChannelMarkupErrorMessage(error)}`, {
          variant: 'error',
        });
        return false;
      }
    },
    [deleteChannelMarkup, enqueueSnackbar, showConfirmDialog],
  );

  return { confirmAndDelete, deleteChannelMarkupMutation };
}
